import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import { adminApi } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n';

interface User {
  id: number;
  email: string;
  role: 'admin' | 'user';
  is_active: boolean;
  created_at: string;
  last_active_at: string | null;
}

const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [isActive, setIsActive] = useState(true);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('admin.failedLoadUsers'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setEmail(user.email);
      setRole(user.role);
      setIsActive(user.is_active);
      setPassword('');
    } else {
      setEditingUser(null);
      setEmail('');
      setPassword('');
      setRole('user');
      setIsActive(true);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setEmail('');
    setPassword('');
    setRole('user');
    setIsActive(true);
  };

  const handleSubmit = async () => {
    setDialogLoading(true);
    try {
      if (editingUser) {
        const updateData: any = { email, role, is_active: isActive };
        if (password) updateData.password = password;
        await adminApi.updateUser(editingUser.id, updateData);
        setSnackbar({
          open: true,
          message: t('admin.userUpdated'),
          severity: 'success',
        });
      } else {
        await adminApi.createUser({ email, password, role, is_active: isActive });
        setSnackbar({
          open: true,
          message: t('admin.userCreated'),
          severity: 'success',
        });
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t('admin.operationFailed')),
        severity: 'error',
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const getErrorMessage = (err: any, fallback: string): string => {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (Array.isArray(detail) && detail.length > 0) {
      return detail.map((e: any) => {
        const field = e.loc && e.loc.length > 1 ? e.loc[e.loc.length - 1] : null;
        const msg = e.msg || e.message || String(e);
        return field ? `${field}: ${msg}` : msg;
      }).join(', ');
    }
    return fallback;
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await adminApi.deleteUser(userToDelete.id);
      setSnackbar({
        open: true,
        message: t('admin.userDeleted'),
        severity: 'success',
      });
      fetchUsers();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t('admin.operationFailed')),
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleRevokeAccess = async (user: User) => {
    try {
      await adminApi.revokeAccess(user.id);
      setSnackbar({
        open: true,
        message: t('admin.accessRevoked'),
        severity: 'success',
      });
      fetchUsers();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t('admin.operationFailed')),
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          {t('admin.users')} ({users.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('admin.addUser')}
        </Button>
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.email')}</TableCell>
                <TableCell>{t('admin.role')}</TableCell>
                <TableCell>{t('admin.status')}</TableCell>
                <TableCell>{t('admin.created')}</TableCell>
                <TableCell>{t('admin.lastActive')}</TableCell>
                <TableCell align="right">{t('admin.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    {user.email}
                    {user.id === currentUser?.id && (
                      <Chip
                        label={t('admin.you')}
                        size="small"
                        sx={{ ml: 1 }}
                        color="primary"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      size="small"
                      color={user.role === 'admin' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? t('admin.active') : t('admin.inactive')}
                      size="small"
                      color={user.is_active ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {user.last_active_at
                      ? new Date(user.last_active_at).toLocaleString()
                      : t('admin.never')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('common.edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {user.id !== currentUser?.id && user.is_active && (
                      <Tooltip title={t('admin.revokeAccess')}>
                        <IconButton
                          size="small"
                          onClick={() => handleRevokeAccess(user)}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {user.id !== currentUser?.id && (
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(user)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? t('admin.editUser') : t('admin.addNewUser')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('admin.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={editingUser ? t('admin.newPasswordHint') : t('login.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required={!editingUser}
            helperText={t('settings.passwordHint')}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('admin.role')}</InputLabel>
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
              label={t('admin.role')}
            >
              <MenuItem value="user">{t('admin.user')}</MenuItem>
              <MenuItem value="admin">{t('nav.admin')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('admin.status')}</InputLabel>
            <Select
              value={isActive ? 'active' : 'inactive'}
              onChange={(e) => setIsActive(e.target.value === 'active')}
              label={t('admin.status')}
            >
              <MenuItem value="active">{t('admin.active')}</MenuItem>
              <MenuItem value="inactive">{t('admin.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={dialogLoading || !email || (!editingUser && !password)}
          >
            {dialogLoading ? <CircularProgress size={24} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('admin.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('admin.deleteConfirmText')}{' '}
            <strong>{userToDelete?.email}</strong>? {t('admin.cannotUndo')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
